import { api } from './apiClient';

/**
 * HR / HCM enterprise expansion client (migration 063). Covers the new
 * command-center, employee-360 depth (documents/movements), lifecycle
 * (separation/F&F/loans/onboarding), talent (performance/L&D/skills/assets),
 * payroll depth (salary structures + GL posting + shifts/regularization/leave
 * policies) and the recruitment ATS. apiClient auto-unwraps {status,data}.
 * The existing hrService.ts (employees/attendance/leave/payroll/recruitment
 * core) stays as-is; this is additive.
 */
export const hrPlusService = {
  // ---- Command center / analytics ----
  commandCenter: (): Promise<any> => api.get('/hr-command/command-center'),
  analytics: (): Promise<any> => api.get('/hr-command/analytics'),
  employee360: (id: number): Promise<any> => api.get(`/hr-command/employee/${id}/360`),
  myDay: (): Promise<any> => api.get('/hr-command/my-day'),

  // ---- Employee documents (on /hr) ----
  getDocuments: (employeeId: number): Promise<{ documents: any[]; count: number }> =>
    api.get(`/hr/employees/${employeeId}/documents`),
  addDocument: (employeeId: number, data: any): Promise<any> =>
    api.post(`/hr/employees/${employeeId}/documents`, data),
  deleteDocument: (id: number): Promise<void> => api.delete(`/hr/documents/${id}`).then(() => undefined),
  expiringDocuments: (days = 30): Promise<any> => api.get('/hr/documents/expiring', { params: { days } }),

  // ---- Employee movements (transfers/promotions/salary-revision) ----
  getMovements: (employeeId: number): Promise<any> => api.get(`/hr/employees/${employeeId}/movements`),
  createMovement: (employeeId: number, data: any): Promise<any> =>
    api.post(`/hr/employees/${employeeId}/movements`, data),
  applyMovement: (id: number): Promise<any> => api.patch(`/hr/movements/${id}/apply`, {}),

  // ---- Lifecycle: separation + F&F ----
  getSeparations: (params?: any): Promise<any> => api.get('/hr-lifecycle/separations', { params }),
  createSeparation: (data: any): Promise<any> => api.post('/hr-lifecycle/separations', data),
  getSeparation: (id: number): Promise<any> => api.get(`/hr-lifecycle/separations/${id}`),
  setSeparationStatus: (id: number, status: string): Promise<any> =>
    api.patch(`/hr-lifecycle/separations/${id}/status`, { status }),
  updateClearance: (id: number, data: any): Promise<any> => api.patch(`/hr-lifecycle/separations/${id}/clearance`, data),
  createFnf: (separationId: number, data: any): Promise<any> =>
    api.post(`/hr-lifecycle/separations/${separationId}/fnf`, data),
  setFnfStatus: (id: number, status: string): Promise<any> => api.patch(`/hr-lifecycle/fnf/${id}/status`, { status }),

  // ---- Lifecycle: loans ----
  getLoans: (params?: any): Promise<any> => api.get('/hr-lifecycle/loans', { params }),
  loanStats: (): Promise<any> => api.get('/hr-lifecycle/loans/stats'),
  employeeLoans: (employeeId: number): Promise<any> => api.get(`/hr-lifecycle/employees/${employeeId}/loans`),
  createLoan: (data: any): Promise<any> => api.post('/hr-lifecycle/loans', data),
  setLoanStatus: (id: number, status: string): Promise<any> => api.patch(`/hr-lifecycle/loans/${id}/status`, { status }),

  // ---- Lifecycle: onboarding ----
  getOnboarding: (employeeId: number): Promise<any> => api.get(`/hr-lifecycle/employees/${employeeId}/onboarding`),
  addOnboarding: (employeeId: number, data: any): Promise<any> =>
    api.post(`/hr-lifecycle/employees/${employeeId}/onboarding`, data),
  setOnboardingStatus: (id: number, status: string): Promise<any> =>
    api.patch(`/hr-lifecycle/onboarding/${id}/status`, { status }),
  overdueOnboarding: (): Promise<any> => api.get('/hr-lifecycle/onboarding/overdue'),

  // ---- Talent: performance ----
  getCycles: (): Promise<any> => api.get('/hr-talent/cycles'),
  createCycle: (data: any): Promise<any> => api.post('/hr-talent/cycles', data),
  setCycleStatus: (id: number, status: string): Promise<any> => api.patch(`/hr-talent/cycles/${id}/status`, { status }),
  getGoals: (params?: any): Promise<any> => api.get('/hr-talent/goals', { params }),
  employeeGoals: (employeeId: number): Promise<any> => api.get(`/hr-talent/employees/${employeeId}/goals`),
  createGoal: (data: any): Promise<any> => api.post('/hr-talent/goals', data),
  updateGoal: (id: number, data: any): Promise<any> => api.put(`/hr-talent/goals/${id}`, data),
  setGoalProgress: (id: number, progress: number): Promise<any> =>
    api.patch(`/hr-talent/goals/${id}/progress`, { progress }),
  getReviews: (params?: any): Promise<any> => api.get('/hr-talent/reviews', { params }),
  employeeReviews: (employeeId: number): Promise<any> => api.get(`/hr-talent/employees/${employeeId}/reviews`),
  createReview: (data: any): Promise<any> => api.post('/hr-talent/reviews', data),
  updateReview: (id: number, data: any): Promise<any> => api.put(`/hr-talent/reviews/${id}`, data),
  performanceDashboard: (): Promise<any> => api.get('/hr-talent/performance/dashboard'),

  // ---- Talent: L&D ----
  getTrainings: (): Promise<any> => api.get('/hr-talent/trainings'),
  createTraining: (data: any): Promise<any> => api.post('/hr-talent/trainings', data),
  getTraining: (id: number): Promise<any> => api.get(`/hr-talent/trainings/${id}`),
  updateTraining: (id: number, data: any): Promise<any> => api.put(`/hr-talent/trainings/${id}`, data),
  setTrainingStatus: (id: number, status: string): Promise<any> =>
    api.patch(`/hr-talent/trainings/${id}/status`, { status }),
  enroll: (trainingId: number, employeeId: number): Promise<any> =>
    api.post(`/hr-talent/trainings/${trainingId}/enroll`, { employeeId }),
  setEnrollmentStatus: (id: number, status: string): Promise<any> =>
    api.patch(`/hr-talent/enrollments/${id}/status`, { status }),
  employeeTrainings: (employeeId: number): Promise<any> => api.get(`/hr-talent/employees/${employeeId}/trainings`),
  trainingDashboard: (): Promise<any> => api.get('/hr-talent/training/dashboard'),

  // ---- Talent: skills ----
  employeeSkills: (employeeId: number): Promise<any> => api.get(`/hr-talent/employees/${employeeId}/skills`),
  addSkill: (employeeId: number, data: any): Promise<any> => api.post(`/hr-talent/employees/${employeeId}/skills`, data),
  updateSkill: (id: number, data: any): Promise<any> => api.put(`/hr-talent/skills/${id}`, data),
  deleteSkill: (id: number): Promise<void> => api.delete(`/hr-talent/skills/${id}`).then(() => undefined),
  skillMatrix: (): Promise<any> => api.get('/hr-talent/skill-matrix'),

  // ---- Talent: assets ----
  getAssets: (params?: any): Promise<any> => api.get('/hr-talent/assets', { params }),
  employeeAssets: (employeeId: number): Promise<any> => api.get(`/hr-talent/employees/${employeeId}/assets`),
  issueAsset: (employeeId: number, data: any): Promise<any> => api.post(`/hr-talent/employees/${employeeId}/assets`, data),
  setAssetStatus: (id: number, status: string): Promise<any> => api.patch(`/hr-talent/assets/${id}/status`, { status }),
  assetStats: (): Promise<any> => api.get('/hr-talent/assets/stats'),

  // ---- Payroll+: salary structures ----
  getSalaryStructures: (): Promise<any> => api.get('/hr-payroll-plus/salary-structures'),
  createSalaryStructure: (data: any): Promise<any> => api.post('/hr-payroll-plus/salary-structures', data),
  getSalaryStructure: (id: number): Promise<any> => api.get(`/hr-payroll-plus/salary-structures/${id}`),
  updateSalaryStructure: (id: number, data: any): Promise<any> => api.put(`/hr-payroll-plus/salary-structures/${id}`, data),
  previewStructure: (id: number, ctc: number): Promise<any> =>
    api.post(`/hr-payroll-plus/salary-structures/${id}/preview`, {}, { params: { ctc } }),
  employeeSalary: (employeeId: number): Promise<any> => api.get(`/hr-payroll-plus/employees/${employeeId}/salary`),
  reviseSalary: (employeeId: number, data: any): Promise<any> =>
    api.post(`/hr-payroll-plus/employees/${employeeId}/salary`, data),
  postPayrollRun: (runId: number): Promise<any> => api.post(`/hr-payroll-plus/payroll-runs/${runId}/post`, {}),

  // ---- Payroll+: shifts / roster / regularization / leave policies ----
  getShifts: (): Promise<any> => api.get('/hr-payroll-plus/shifts'),
  createShift: (data: any): Promise<any> => api.post('/hr-payroll-plus/shifts', data),
  updateShift: (id: number, data: any): Promise<any> => api.put(`/hr-payroll-plus/shifts/${id}`, data),
  getRoster: (params?: any): Promise<any> => api.get('/hr-payroll-plus/roster', { params }),
  assignRoster: (data: any): Promise<any> => api.post('/hr-payroll-plus/roster', data),
  getRegularizations: (params?: any): Promise<any> => api.get('/hr-payroll-plus/regularizations', { params }),
  createRegularization: (employeeId: number, data: any): Promise<any> =>
    api.post(`/hr-payroll-plus/employees/${employeeId}/regularizations`, data),
  setRegularizationStatus: (id: number, status: string): Promise<any> =>
    api.patch(`/hr-payroll-plus/regularizations/${id}/status`, { status }),
  getLeavePolicies: (): Promise<any> => api.get('/hr-payroll-plus/leave-policies'),
  createLeavePolicy: (data: any): Promise<any> => api.post('/hr-payroll-plus/leave-policies', data),
  updateLeavePolicy: (id: number, data: any): Promise<any> => api.put(`/hr-payroll-plus/leave-policies/${id}`, data),

  // ---- Recruitment ATS ----
  getRequisitions: (params?: any): Promise<any> => api.get('/hr-recruitment/requisitions', { params }),
  createRequisition: (data: any): Promise<any> => api.post('/hr-recruitment/requisitions', data),
  updateRequisition: (id: number, data: any): Promise<any> => api.put(`/hr-recruitment/requisitions/${id}`, data),
  setRequisitionStatus: (id: number, status: string): Promise<any> =>
    api.patch(`/hr-recruitment/requisitions/${id}/status`, { status }),
  pipeline: (): Promise<any> => api.get('/hr-recruitment/pipeline'),
  getApplication: (id: number): Promise<any> => api.get(`/hr-recruitment/applications/${id}`),
  updateApplication: (id: number, data: any): Promise<any> => api.put(`/hr-recruitment/applications/${id}`, data),
  moveApplicationStage: (id: number, stage: string): Promise<any> =>
    api.patch(`/hr-recruitment/applications/${id}/stage`, { stage }),
  getInterviews: (applicationId: number): Promise<any> =>
    api.get(`/hr-recruitment/applications/${applicationId}/interviews`),
  scheduleInterview: (applicationId: number, data: any): Promise<any> =>
    api.post(`/hr-recruitment/applications/${applicationId}/interviews`, data),
  updateInterview: (id: number, data: any): Promise<any> => api.patch(`/hr-recruitment/interviews/${id}`, data),
  getOffer: (applicationId: number): Promise<any> => api.get(`/hr-recruitment/applications/${applicationId}/offer`),
  createOffer: (applicationId: number, data: any): Promise<any> =>
    api.post(`/hr-recruitment/applications/${applicationId}/offer`, data),
  setOfferStatus: (id: number, status: string): Promise<any> =>
    api.patch(`/hr-recruitment/offers/${id}/status`, { status }),
  convertCandidate: (applicationId: number): Promise<any> =>
    api.post(`/hr-recruitment/applications/${applicationId}/convert`, {}),
  getLocations: (): Promise<any> => api.get('/hr-recruitment/locations'),
  createLocation: (data: any): Promise<any> => api.post('/hr-recruitment/locations', data),
  recruitmentAnalytics: (): Promise<any> => api.get('/hr-recruitment/recruitment/analytics'),
};

export default hrPlusService;
